package one.devsbox.www.ui.devices

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.DeviceOutline
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode
fun ListingDevicesUI() {
    with(ListingDevicesCtx.get()) {
        ContentListingDevicesUI(Modifier.fillMaxSize())
    }
}

@Composable
context(ctx: ListingDevicesCtx)
private fun ContentListingDevicesUI(modifier: Modifier = Modifier) {
    Column(modifier = modifier.padding(16.dp)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> {
                    SuccessUI(state.nnvl)
                }
            }
        }
    }
}

@Composable
context(ctx: ListingDevicesCtx)
private fun BrandingAndStatsBar(devicesResult: SharedIO.Devices) {
    val dp = LocalDisplayProvider.current.state
    if (dp.smallScreen) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = stringResource(Res.string.ui_devices_listing_title),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onBackground
                )
                Spacer(Modifier.weight(1f))
                EntryViewSwitch(ctx.prefs.deviceLayoutType) {
                    ctx.prefs.deviceLayoutType = it
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_total),
                    value = devicesResult.count.toString(),
                    valueColor = MaterialTheme.colorScheme.onSurface
                )
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_online),
                    value = devicesResult.online.toString(),
                    valueColor = MaterialTheme.colorScheme.primary
                )
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_offline),
                    value = (devicesResult.count - devicesResult.online).toString(),
                    valueColor = MaterialTheme.colorScheme.error
                )
            }
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Bottom
        ) {
            Text(
                text = stringResource(Res.string.ui_devices_listing_title),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onBackground
            )
            Spacer(Modifier.weight(1f))

            Row(
                horizontalArrangement = Arrangement.spacedBy(32.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_total),
                    value = devicesResult.count.toString(),
                    valueColor = MaterialTheme.colorScheme.onSurface
                )
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_online),
                    value = devicesResult.online.toString(),
                    valueColor = MaterialTheme.colorScheme.primary
                )
                StatItem(
                    label = stringResource(Res.string.ui_devices_listing_stat_offline),
                    value = (devicesResult.count - devicesResult.online).toString(),
                    valueColor = MaterialTheme.colorScheme.error
                )
            }

            Spacer(Modifier.weight(1f))

            EntryViewSwitch(ctx.prefs.deviceLayoutType) {
                ctx.prefs.deviceLayoutType = it
            }
        }
    }
}

@Composable
context(ctx: ListingDevicesCtx)
private fun SuccessUI(devicesResult: SharedIO.Devices) {
    if (devicesResult.count == 0) {
        CategorySplashUI(
            icon = Svg.DeviceOutline,
            title = Res.string.ui_devices_splash_title,
            explanation = Res.string.ui_devices_splash_desc
        )
    } else {
        Column(modifier = Modifier.fillMaxSize()) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                SearchBox(
                    state = ctx.vm.search,
                    modifier = Modifier.weight(1f),
                    hint = stringResource(Res.string.ui_devices_listing_placeholder_search)
                )
            }
            Spacer(Modifier.height(16.dp))

            BrandingAndStatsBar(devicesResult)

            Spacer(Modifier.height(16.dp))

            val showingStart = if (devicesResult.items.isEmpty()) 0 else 1
            val showingEnd = devicesResult.items.size
            val totalCount = devicesResult.count

            Text(
                text = stringResource(
                    Res.string.ui_devices_listing_showing,
                    showingStart,
                    showingEnd,
                    totalCount
                ),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                letterSpacing = 1.sp,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            DevicesListing(
                devices = devicesResult.items,
                search = ctx.vm.search,
                totalCount = devicesResult.ids.size,
                loadingMore = ctx.vm.loadingMore,
                onLoadMore = { ctx.action.loadMore() }
            ) {
                ctx.action.openDetail(it.id)
            }
        }
    }
}

@Composable
private fun StatItem(label: String, value: String, valueColor: Color) {
    Column(horizontalAlignment = Alignment.End) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
            color = valueColor,
            modifier = Modifier.align(Alignment.CenterHorizontally)
        )
    }
}
